import type { BrandProfile } from '@/types/portal';

export interface BrandCompleteness {
  filled: number;
  total: number;
  percent: number;
}

/** How complete the core agency profile is, used for the completeness KPI. */
export function brandCompleteness(profile: BrandProfile): BrandCompleteness {
  const fields = [
    profile.name,
    profile.legalName,
    profile.tagline,
    profile.supportEmail,
    profile.website,
    profile.baseCurrency,
  ];
  const total = fields.length;
  const filled = fields.filter((value) => String(value).trim().length > 0).length;
  return { filled, total, percent: total > 0 ? Math.round((filled / total) * 100) : 0 };
}

/** Pick a readable text colour for a swatch so labels stay legible on any hex. */
export function readableTextOn(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) || 0;
  const g = parseInt(clean.slice(2, 4), 16) || 0;
  const b = parseInt(clean.slice(4, 6), 16) || 0;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#1F1B17' : '#FFFFFF';
}

/**
 * Normalise a hand-typed hex string. Accepts #RGB, #RRGGBB with or without the
 * leading hash and returns a clean upper-case #RRGGBB, or null when invalid.
 */
export function normalizeHex(input: string): string | null {
  let value = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(value)) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (/^[0-9a-fA-F]{6}$/.test(value)) {
    return `#${value.toUpperCase()}`;
  }
  return null;
}

/** A soft translucent version of a brand colour for tinted preview surfaces. */
export function softTintOn(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) || 0;
  const g = parseInt(clean.slice(2, 4), 16) || 0;
  const b = parseInt(clean.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}